import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CLP } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateField } from "@/components/DateField";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tag, Loader2, Trash2, Pencil, Plus, X, AlertTriangle, CalendarRange, Infinity as InfinityIcon, Check, Power } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => d ? d.split("-").reverse().join("-") : "";
const blank = { discount_pct: "", start_date: "", end_date: "" };
const STEPS = Array.from({ length: 20 }, (_, i) => (i + 1) * 5);
const finalPrice = (price, pct) => Math.round(price * (1 - pct / 100));
const PriceLabel = ({ price, pct }) => finalPrice(price, pct) === 0
  ? <span className="font-bold text-emerald-600">GRATIS</span>
  : <span className="font-bold text-primary">{CLP(finalPrice(price, pct))}</span>;

const PricePreview = ({ price, pct }) => {
  const p = Number(pct) || 0;
  if (p < 1 || p > 99) return <p className="text-xs text-muted-foreground">Ingresa un descuento entre 1% y 99%.</p>;
  return (
    <div className="flex items-center gap-2 text-sm" data-testid="offer-price-preview">
      <span className="line-through text-muted-foreground">{CLP(price)}</span>
      <span className="text-lg"><PriceLabel price={price} pct={p} /></span>
      <span className="rounded-full bg-coral/15 text-coral text-xs font-semibold px-2 py-0.5">-{p}%</span>
    </div>
  );
};

function OfferForm({ data, initial, onSaved, onCancel, workshopId }) {
  const [f, setF] = useState(initial || blank);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const pct = Number(f.discount_pct);
    if (!f.start_date || !f.end_date) { toast.error("Indica las fechas de inicio y término"); return; }
    if (!(pct >= 1 && pct <= 99)) { toast.error("El descuento debe estar entre 1% y 99%"); return; }
    if (f.start_date > f.end_date) { toast.error("La fecha de inicio no puede ser posterior a la de término"); return; }
    setBusy(true);
    try {
      const body = { discount_pct: pct, start_date: f.start_date, end_date: f.end_date };
      if (initial?.id) await api.put(`/business/workshops/${workshopId}/offers/${initial.id}`, body);
      else await api.post(`/business/workshops/${workshopId}/offers`, body);
      toast.success(initial?.id ? "Periodo de descuento actualizado" : "Periodo de descuento creado");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="offer-form">
      <div className="grid grid-cols-1 gap-3">
        <div><Label>Inicio</Label>
          <DateField min={data.today} max={data.first_session_date} value={f.start_date}
            onChange={(v) => setF({ ...f, start_date: v, end_date: f.end_date && f.end_date < v ? "" : f.end_date })} testId="offer-start" /></div>
        <div><Label>Término</Label>
          <DateField min={f.start_date || data.today} max={data.first_session_date} value={f.end_date}
            onChange={(v) => setF({ ...f, end_date: v })} testId="offer-end" /></div>
      </div>
      <div><Label>Descuento (%)</Label>
        <Input inputMode="numeric" placeholder="Ej: 30" value={f.discount_pct} className="max-w-[120px]"
          onChange={(e) => setF({ ...f, discount_pct: e.target.value.replace(/\D/g, "").slice(0, 2) })} data-testid="offer-pct" /></div>
      <PricePreview price={data.price_clp} pct={f.discount_pct} />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="offer-cancel"><X className="w-4 h-4 mr-1" />Cancelar</Button>
        <Button size="sm" onClick={save} disabled={busy} data-testid="offer-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar periodo"}
        </Button>
      </div>
    </div>
  );
}

const OfferRow = ({ o, price, onEdit, onDelete }) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border p-3 bg-background" data-testid={`offer-row-${o.id}`}>
    <div className="min-w-0">
      <p className="text-sm font-semibold">{fmt(o.start_date)} al {fmt(o.end_date)}</p>
      <p className="text-xs text-muted-foreground">
        <span className="text-coral font-semibold">-{o.discount_pct}%</span> · <PriceLabel price={price} pct={o.discount_pct} />
      </p>
    </div>
    <div className="flex gap-1 shrink-0">
      <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`offer-edit-${o.id}`}><Pencil className="w-4 h-4" /></Button>
      <Button variant="ghost" size="icon" onClick={onDelete} className="text-destructive" data-testid={`offer-delete-${o.id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  </div>
);

function PeriodsPanel({ data, workshopId, reload }) {
  const [editing, setEditing] = useState(null);
  const remove = async (o) => {
    try { await api.delete(`/business/workshops/${workshopId}/offers/${o.id}`); toast.success("Periodo eliminado"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const done = () => { setEditing(null); reload(); };
  return (
    <div className="space-y-3" data-testid="periods-panel">
      <p className="text-xs text-muted-foreground">Desde hoy hasta el día de la primera sesión ({fmt(data.first_session_date)}). Los periodos no pueden cruzarse y se activan solos.</p>
      {data.offers.map((o) => editing?.id === o.id
        ? <OfferForm key={o.id} data={data} initial={o} workshopId={workshopId} onSaved={done} onCancel={() => setEditing(null)} />
        : <OfferRow key={o.id} o={o} price={data.price_clp} onEdit={() => setEditing(o)} onDelete={() => remove(o)} />)}
      {data.offers.length === 0 && editing !== "new" && (
        <p className="text-sm text-muted-foreground text-center py-2" data-testid="offers-empty">Aún no tienes periodos de descuento.</p>
      )}
      {editing === "new"
        ? <OfferForm data={data} workshopId={workshopId} onSaved={done} onCancel={() => setEditing(null)} />
        : <Button variant="outline" className="w-full" onClick={() => setEditing("new")} data-testid="offer-add-btn"><Plus className="w-4 h-4 mr-1" />Agregar periodo de descuento</Button>}
    </div>
  );
}

const PctPicker = ({ value, onChange }) => (
  <div className="grid grid-cols-5 gap-1.5" data-testid="permanent-pct-picker">
    {STEPS.map((p) => (
      <button key={p} type="button" onClick={() => onChange(p)} data-testid={`permanent-pct-${p}`}
        className={`rounded-lg border py-1.5 text-sm font-semibold transition-colors ${value === p ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:border-primary/60"}`}>
        {p}%
      </button>
    ))}
  </div>
);

const PermanentRow = ({ d, price, active, onActivate, onDelete }) => (
  <div className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${active ? "border-emerald-500 bg-emerald-50" : "bg-background"}`} data-testid={`permanent-row-${d.id}`}>
    <div className="min-w-0 text-sm">
      <p className="font-semibold"><span className="text-coral">-{d.pct}%</span> · <PriceLabel price={price} pct={d.pct} /></p>
      <p className="text-xs text-muted-foreground">{active ? "Activo ahora" : "Guardado, sin aplicar"}</p>
    </div>
    <div className="flex gap-1 shrink-0">
      {!active && <Button variant="outline" size="sm" onClick={onActivate} data-testid={`permanent-activate-${d.id}`}><Power className="w-4 h-4 mr-1" />Activar</Button>}
      {active && <span className="inline-flex items-center text-xs font-semibold text-emerald-700 px-2" data-testid={`permanent-active-${d.id}`}><Check className="w-4 h-4 mr-1" />Activo</span>}
      <Button variant="ghost" size="icon" onClick={onDelete} className="text-destructive" data-testid={`permanent-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  </div>
);

function PermanentPanel({ data, workshopId, reload }) {
  const [pct, setPct] = useState(null);
  const [busy, setBusy] = useState(false);
  const call = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setPct(null); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const base = `/business/workshops/${workshopId}/permanent-discounts`;
  return (
    <div className="space-y-3" data-testid="permanent-panel">
      <p className="text-xs text-muted-foreground">Se aplica desde hoy y se mantiene hasta que tú lo desactives o lo cambies. Solo un descuento puede estar activo.</p>
      <PctPicker value={pct} onChange={setPct} />
      {pct && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="permanent-preview">
          <span className="text-sm"><span className="line-through text-muted-foreground mr-2">{CLP(data.price_clp)}</span><PriceLabel price={data.price_clp} pct={pct} /></span>
          <Button size="sm" disabled={busy} onClick={() => call(() => api.post(base, { pct }), `Descuento de ${pct}% activado`)} data-testid="permanent-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Aplicar ${pct}%`}
          </Button>
        </div>
      )}
      {data.permanent_discounts.map((d) => (
        <PermanentRow key={d.id} d={d} price={data.price_clp} active={d.id === data.permanent_active_id}
          onActivate={() => call(() => api.post(`${base}/${d.id}/activate`), `Descuento de ${d.pct}% activado`)}
          onDelete={() => call(() => api.delete(`${base}/${d.id}`), "Descuento eliminado")} />
      ))}
      {data.permanent_active_id && (
        <Button variant="outline" className="w-full" disabled={busy} onClick={() => call(() => api.post(`${base}/deactivate`), "Descuento desactivado")} data-testid="permanent-deactivate">
          <Power className="w-4 h-4 mr-1" />Desactivar descuento
        </Button>
      )}
    </div>
  );
}

const ModeOption = ({ id, icon: Icon, title, text, selected, onSelect }) => (
  <button type="button" onClick={onSelect} data-testid={`discount-mode-${id}`}
    className={`flex-1 text-left rounded-xl border-2 p-3 transition-colors ${selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
    <span className="flex items-center gap-2 font-semibold text-sm">
      <span className={`w-4 h-4 rounded-full border-2 grid place-items-center ${selected ? "border-primary" : "border-muted-foreground/40"}`}>
        {selected && <span className="w-2 h-2 rounded-full bg-primary" />}
      </span>
      <Icon className="w-4 h-4 text-primary" />{title}
    </span>
    <span className="block text-xs text-muted-foreground mt-1">{text}</span>
  </button>
);

export function OffersDialog({ workshop, onClose }) {
  const [data, setData] = useState(null);
  const load = () => api.get(`/business/workshops/${workshop.id}/offers`).then((r) => setData(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const mode = data?.discount_mode;
  const choose = async (m) => {
    if (m === mode) return;
    try {
      await api.put(`/business/workshops/${workshop.id}/discount-mode`, { mode: m });
      if (mode) toast.info(m === "permanent" ? "Ahora usas Descuento permanente. Tus periodos quedan guardados sin aplicar." : "Ahora usas Descuento por periodos. Tus descuentos permanentes quedan guardados sin aplicar.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="offers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Tag className="w-5 h-5 text-coral shrink-0" />Descuento</DialogTitle>
          <DialogDescription className="font-semibold text-foreground break-words" data-testid="discount-workshop-name">{workshop.name}</DialogDescription>
        </DialogHeader>
        {!data ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /> : !data.eligible ? (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="offers-not-eligible">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{data.reason}</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-2">Precio normal {CLP(data.price_clp)}. Elige una de las dos opciones; solo una se aplica a la vez.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <ModeOption id="periods" icon={CalendarRange} title="Por periodos" text="Fechas de inicio y término antes de la primera sesión." selected={mode === "periods"} onSelect={() => choose("periods")} />
                <ModeOption id="permanent" icon={InfinityIcon} title="Permanente" text="De 5% en 5% hasta 100%. Lo activas o cambias cuando quieras." selected={mode === "permanent"} onSelect={() => choose("permanent")} />
              </div>
            </div>
            {mode === "periods" && <PeriodsPanel data={data} workshopId={workshop.id} reload={load} />}
            {mode === "permanent" && <PermanentPanel data={data} workshopId={workshop.id} reload={load} />}
            {!mode && <p className="text-sm text-muted-foreground text-center py-2" data-testid="discount-mode-empty">Elige una opción para empezar.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
